import React, { useState, useEffect } from 'react';

import API from '../../services/api.js';
import { usePolling } from '../../services/PollingContext.js';

export default function Review({ generateYaml, handleScan }) {
  const { showNotification } = usePolling();
  const [redteamProviders, setRedteamProviders] = useState([]);
  const [redteamProviderKey, setRedteamProviderKey] = useState("");
  const [providersLoading, setProvidersLoading] = useState(false);
  
  const [isRunning, setIsRunning] = useState(() => {
    const saved = sessionStorage.getItem("scanIsRunning");
    
    return saved ? JSON.parse(saved) : false;
  });
  const [taskId, setTaskId] = useState(() => sessionStorage.getItem("scanTaskId"));
  const [failedScan, setFailedScan] = useState(() => {
    const saved = sessionStorage.getItem("failedScan");
    return saved ? JSON.parse(saved) : null;
  });
  const [scanDepth, setScanDepth] = useState(() => {
    const saved = sessionStorage.getItem("savedScanDepth");
    return saved ? parseInt(saved) : null;
  });
  const [concurrency, setConcurrency] = useState(() => {
    const saved = sessionStorage.getItem("savedConcurrency");
    return saved ? parseInt(saved) : 15;
  });


  useEffect(() => {
    let intervalId;

    const pollTaskStatus = async () => {
      if (!taskId) return;
      try {
        const response = await API.get(`/task-status/${taskId}/`);
        const data = response.data;
        if (data.status === 'SUCCESS') {
          setIsRunning(false);
          setTaskId(null);
          setFailedScan(null);
          sessionStorage.removeItem("scanIsRunning");
          sessionStorage.removeItem("scanTaskId");
          sessionStorage.removeItem("failedScan");
          showNotification(`Scan with Job ID: ${data.result?.job_id} completed successfully.`, 'success');
          clearInterval(intervalId);
        } else if (data.status === 'FAILURE') {
          setIsRunning(false);
          setTaskId(null);
          sessionStorage.removeItem("scanIsRunning");
          sessionStorage.removeItem("scanTaskId");
          if (data.resumable && data.scan_id) {
            const fs = { scan_id: data.scan_id, failed_phase: data.failed_phase };
            setFailedScan(fs);
            sessionStorage.setItem("failedScan", JSON.stringify(fs));
            showNotification(`Scan failed during ${data.failed_phase || 'processing'} — you can resume it.`, 'error');
          } else {
            setFailedScan(null);
            sessionStorage.removeItem("failedScan");
            showNotification(`Scan failed: ${data.message || 'unknown error'}`, 'error');
          }
          clearInterval(intervalId);
        }
      } catch (error) {
        // Transient network/poll error — do NOT wipe scan state; the next tick retries.
        console.error("Error polling task status:", error);
      }
    };

    if (isRunning && taskId) {
      intervalId = setInterval(pollTaskStatus, 5000);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [isRunning, taskId, showNotification]);

  useEffect(() => {
    const loadProviders = async () => {
      setProvidersLoading(true);
      try {
        const response = await API.get('/ai-providers/');
        const providers = response?.data?.providers || [];
        setRedteamProviders(providers);
        if (providers.length > 0) {
          const preferred = providers.find((p) => p.key === 'openai') || providers[0];
          setRedteamProviderKey(preferred.key);
        }
      } catch (error) {
        console.error("Failed to load providers:", error);
      } finally {
        setProvidersLoading(false);
      }
    };
    loadProviders();
  }, []);

  const appData = sessionStorage.getItem("applicationData");
  const tarData = sessionStorage.getItem("targetData");
  const selectedMethods = sessionStorage.getItem("selectedMethods");

  const [detectorsList, setDetectorsList] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("selectedDetectors")) || [];
    } catch {
      return [];
    }
  });

  const [customDetectorsState, setCustomDetectorsState] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("customDetectors") || "{}");
    } catch {
      return {};
    }
  });

  const [customStrategiesState] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("customStrategies") || "{}");
    } catch {
      return {};
    }
  });

  // Strategy config schema (persisted by the Methods step) — used to show the
  // user-facing field labels (e.g. "Streaming") rather than raw keys ("stateful").
  const [strategyConfigSchema] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("strategyConfigSchema") || "{}");
    } catch {
      return {};
    }
  });

  const [customPrompts] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("customPrompts") || "[]");
    } catch {
      return [];
    }
  });

  const applicationData = JSON.parse(appData);
  const targetData = JSON.parse(tarData);
  const methods = JSON.parse(selectedMethods);

  const [description, setDescription] = useState(() => {
    const savedDescription = sessionStorage.getItem("description");
    return savedDescription?.replace(/^"|"$/g, '') || '';
  });

  const downloadYaml = async () => {
    if (!redteamProviderKey) {
      alert('Please select a provider');
      return;
    }
    const response = await API.post('/generate-yaml/', {
      application_data: applicationData,
      target_data: targetData,
      detectors: detectorsList,
      custom_detectors: customDetectorsState,
      custom_prompts: customPrompts,
      methods: methods,
      custom_strategies: customStrategiesState,
      config_name: description,
      scanDepth: scanDepth,
      provider_key: redteamProviderKey,
      concurrency: concurrency,
    });

    const data = response.data;
    const blob = new Blob([data.yaml_config], { type: 'text/yaml' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'redteam-config.yaml';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const runConfig = async () => {
    if (!scanDepth) {
      alert('Please select a scan type');
      return;
    }
    if (!redteamProviderKey) {
      alert('Please select a provider');
      return;
    }

    setIsRunning(true);
    sessionStorage.setItem("scanIsRunning", JSON.stringify(true));

    try {
      const scanResponse = await API.post('/thehandler/scan/', {
        application_data: applicationData,
        target_data: targetData,
        detectors: detectorsList,
        custom_detectors: customDetectorsState,
        custom_prompts: customPrompts,
        methods: methods,
        custom_strategies: customStrategiesState,
        config_name: description,
        scanDepth: scanDepth,
        provider_key: redteamProviderKey,
        concurrency: concurrency,
      });

      const scanData = scanResponse.data;
      if (scanData.task_id) {
        setTaskId(scanData.task_id);
        sessionStorage.setItem("scanTaskId", scanData.task_id);
        showNotification("Scan started successfully.", "info");
      } else {
        throw new Error(scanData.message || 'Scan failed to start');
      }
    } catch (error) {
      const detail = error.response?.data?.Error || error.response?.data?.error || error.message;
      alert(`Failed to start scan: ${detail}`);
      setIsRunning(false);
      sessionStorage.removeItem("scanIsRunning");
    }
  };

  const handleCancel = async () => {
    if (!taskId) return;
    try {
      await API.post(`/cancel-task/${taskId}/`);

      showNotification('Scan cancelled successfully', "info");
      setIsRunning(false);
      setTaskId(null);
      sessionStorage.removeItem("scanIsRunning");
      sessionStorage.removeItem("scanTaskId");
    } catch (error) {
      alert(`Error cancelling scan: ${error.message}`);
    }
  };

  const handleResume = async () => {
    if (!failedScan?.scan_id) return;
    try {
      const response = await API.post('/thehandler/scan/resume/', { scan_id: failedScan.scan_id });
      const data = response.data;
      if (data.task_id) {
        setFailedScan(null);
        sessionStorage.removeItem("failedScan");
        setTaskId(data.task_id);
        sessionStorage.setItem("scanTaskId", data.task_id);
        setIsRunning(true);
        sessionStorage.setItem("scanIsRunning", JSON.stringify(true));
        showNotification(`Resuming scan from ${data.resume_from || 'last checkpoint'}…`, 'info');
      } else {
        throw new Error(data.error || 'Could not resume scan');
      }
    } catch (error) {
      alert(`Failed to resume scan: ${error.response?.data?.error || error.message}`);
    }
  };

  const removeDetector = (detectorToRemove) => {
    const updated = detectorsList.filter(d => d !== detectorToRemove);
    setDetectorsList(updated);
    sessionStorage.setItem("selectedDetectors", JSON.stringify(updated));

    const updatedCustomDetectors = { ...customDetectorsState };
    delete updatedCustomDetectors[detectorToRemove];
    setCustomDetectorsState(updatedCustomDetectors);
    sessionStorage.setItem("customDetectors", JSON.stringify(updatedCustomDetectors));
  };

  return (
    <div className="space-y-6 text-white m-12">
      <h2 className="text-xl font-bold">Review Your Configuration</h2>

      <div className="flex gap-4">
        {[5, 10, 25].map(depth => (
          <button
            key={depth}
            onClick={() => {
              setScanDepth(depth);
              sessionStorage.setItem("savedScanDepth", depth.toString());
            }}
            className={`rounded-full px-4 py-2 hover:bg-blue-600/20 hover:text-blue-400 transition-all duration-200 cursor-pointer font-semibold ${scanDepth === depth ? 'bg-blue-600/20 border border-blue-400 text-blue-400' : 'bg-transparent border border-[#232325]'}`}
          >
            {depth === 5 ? 'Quick Scan' : depth === 10 ? 'Normal Scan' : 'Deep Scan'}
          </button>
        ))}
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">Max Concurrency</h3>
        <p className="text-gray-400 text-sm mb-2">Concurrent requests to the target (1–15). Lower it for slow or self-hosted models that time out under load.</p>
        <input
          type="number"
          min={1}
          max={15}
          value={concurrency}
          onChange={(e) => {
            const v = Math.max(1, Math.min(15, parseInt(e.target.value) || 1));
            setConcurrency(v);
            sessionStorage.setItem("savedConcurrency", v.toString());
          }}
          className="w-24 bg-[#101012] border border-[#232325] rounded-lg px-3 py-2 text-white focus:border-blue-400 focus:outline-none"
        />
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">Configuration Name</h3>
        <input
          type="text"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            sessionStorage.setItem("description", e.target.value);
          }}
          className={`w-full text-white border p-2 rounded-xl transition-colors duration-200 
            ${description.trim() === '' 
              ? 'bg-[#2a0e0e] border-red-500 placeholder-red-400' 
              : 'bg-[#101012] border-[#232325]'}`}
          placeholder="Name of your configuration file"
        />
        {description.trim() === '' && (
          <p className="text-red-400 text-sm mt-1">Configuration name is required.</p>
        )}
      </div>


      <div className="grid grid-cols-2 gap-4">
        <div className="bg-[#101012] text-white border border-[#232325] p-4 rounded-xl">
          <h3 className="font-semibold mb-2">
            Detectors: {detectorsList?.length > 0 && (<span>{detectorsList.length}</span>)}
          </h3>
          {Array.isArray(detectorsList) && detectorsList.length > 0 && (
            <div className="flex flex-wrap gap-2">
            {detectorsList.map((detector, index) => {
              const cfg = customDetectorsState[detector]?.config || {};
              return (
                <span
                  key={index}
                  className="group flex items-center gap-2 bg-blue-600/20 border border-blue-400 text-blue-400 px-2 py-1 rounded-xl text-sm"
                >
                  <span>
                    {detector}
                    {Object.keys(cfg).length > 0 && (
                      <> ( {Object.entries(cfg).map(([k, v]) => `${k}: ${v}`).join(", ")} )</>
                    )}
                  </span>
                  <button
                    onClick={() => removeDetector(detector)}
                    className="ml-1 text-red-400 hover:text-red-200 font-bold opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    title="Remove detector"
                  >
                    ×
                  </button>
                </span>
              );
            })}
            </div>
          )}
        </div>

        <div className="bg-[#101012] text-white border border-[#232325] p-4 rounded-xl">
          <h3 className="font-semibold mb-2">
            Methods: {methods?.length > 0 && (<span>{methods.length}</span>)}
          </h3>
          <div className="flex flex-wrap gap-2">
            {Array.isArray(methods) && methods.map((method, index) => {
              const cfg = customStrategiesState[method]?.config;
              return (
                <span
                  key={index}
                  className="bg-blue-600/20 border border-blue-400 text-blue-400 px-2 py-1 rounded-xl text-sm"
                >
                  {method}
                  {cfg && Object.keys(cfg).length > 0 && (
                    <>
                      {" "}
                      (
                      {Object.entries(cfg)
                        .map(([k, v]) => {
                          const f = (
                            strategyConfigSchema[method]?.fields || []
                          ).find((x) => x.key === k);
                          const val =
                            typeof v === "boolean" ? (v ? "on" : "off") : v;
                          return `${f?.label || k}: ${val}`;
                        })
                        .join(", ")}
                      )
                    </>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      </div>

      <div className="bg-[#101012] text-white border border-[#232325] p-4 rounded-xl">
        <h3 className="font-semibold mb-2">Application Details</h3>
        <div className="space-y-2">
          <p><strong>Purpose:</strong> {applicationData?.purpose}</p>
          <p><strong>User:</strong> {applicationData?.user_description}</p>
          <p><strong>Connected Systems:</strong> {applicationData?.connected_systems}</p>
          <p><strong>Accessible Data:</strong> {applicationData?.accessible_data}</p>
          <p><strong>Forbidden Data:</strong> {applicationData?.forbidden_data}</p>
          <p><strong>Allowed Actions:</strong> {applicationData?.allowed_actions}</p>
          <p><strong>Forbidden Actions:</strong> {applicationData?.forbidden_actions}</p>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Running Your Configuration</h3>
        <div className="mb-4">
          <label className="block text-sm font-medium">Select red-team provider:</label>
          {providersLoading ? (
            <p className="text-gray-400 text-sm mt-2">Loading providers...</p>
          ) : redteamProviders.length === 0 ? (
            <p className="text-gray-400 text-sm mt-2">No providers configured.</p>
          ) : (
            <div className="flex items-center space-x-8 m-6 ml-0 flex-wrap gap-y-3">
              {redteamProviders.map((p) => (
                <div
                  key={p.key}
                  className="flex items-center cursor-pointer"
                  onClick={() => setRedteamProviderKey(p.key)}
                >
                  <div className={`w-4 h-4 rounded-full mr-2 ${redteamProviderKey === p.key ? "bg-blue-500" : "border-2 border-blue-500"}`}></div>
                  <span className="text-white">{p.redteam_label || p.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mb-4">
          <div className="flex gap-2">
            <button
              onClick={runConfig}
              disabled={isRunning || description.trim() === ''}
              className={`px-4 py-2 rounded-xl border cursor-pointer transition-all duration-200 
                ${isRunning || description.trim() === '' 
                  ? 'bg-gray-700 border-gray-500 text-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600/20 border-blue-400 text-blue-400 hover:bg-blue-600/30'}`}
            >
              {isRunning ? "Running Scan" : "Run Now"}
            </button>

            {isRunning && (
              <button onClick={handleCancel} className="bg-red-600/20 border border-red-400 text-red-400 cursor-pointer px-4 py-2 rounded-xl ">
                Cancel Scan
              </button>
            )}

            {!isRunning && failedScan && (
              <button onClick={handleResume} className="bg-amber-600/20 border border-amber-400 text-amber-400 cursor-pointer px-4 py-2 rounded-xl">
                {failedScan.failed_phase ? `Resume Scan (from ${failedScan.failed_phase})` : 'Resume Scan'}
              </button>
            )}
            <button onClick={downloadYaml} className="bg-blue-700/20 border-blue-400 text-blue-400 border cursor-pointer px-4 py-2 rounded-xl ">
              Download YAML
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
